// Put material into the selected piece's hands, a grade at a time.
//
// **The half of the game that takes longest to reach.** A stocked foundry, a
// full oven or a warehouse worth emptying is twenty turns of honest mining
// away, so everything downstream of it — a recipe, a turret climbing its
// ladder, a store's taps doing something — is expensive to look at even once.
// That cost lands hardest on exactly the features nobody has played much.
//
// A **command** rather than a write into `industry`, like every other tool
// here: the log is the save format, undo, replay and the network protocol, so a
// hand-stocked buffer is a board that cannot be replayed and a hash that
// disagrees at the next sync. `dev-material` is in `SOLO_COMMANDS`.
//
// **It acts on the selection rather than carrying a square picker**, because
// the board already has one and a second way to say "this piece" is a second
// thing that can disagree with what is highlighted. With nothing selected it
// says so rather than vanishing — a dev control that disappears is a control
// you go looking for, which is the rule `BeatArmy` states about its last army.

// What stays on the engine: **the ladders** — which (line, rung) pairs this
// game can hold and what each is called — is the materials table, and the
// contract has no `catalog.materials()` yet. Everything the square *holds* is
// read off `game.board.at`.
import { GRADE_SLOTS } from "src/engine/rules/industry/state";
import { LADDERS } from "src/engine/rules/materials";
import { LINE_INDEX } from "src/client/ai/render/factNames";
import { METAL, SPECIAL, STONE } from "src/shared/industry";
import type { DevDispatch } from "../DevMenu";
import { Btn, styled, Text } from "../kit";
import { useGame } from "../useGame";
import { useSessionView } from "../useSessionView";
import { UNIT_TONE } from "../unitTone";
import { Fold } from "./parts";

/** One rung of one ladder. Drawn a dozen times, so a `styled` and not `sx`. */
const Rung = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.xs
}));

/**
 * The material's name, in the material's own colour.
 *
 * `UNIT_TONE` and not a token: a material has to be the same colour here as it
 * is on a belt, so this reads the board's own tables.
 */
const Named = styled("span")(({ theme }) => ({
  width: 92,
  flexShrink: 0,
  fontFamily: theme.kit.font.body,
  fontWeight: 700,
  fontSize: 12,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
}));

/** How many are held, beside the buttons that change it. */
const Count = styled(Text)({ width: 22, textAlign: "right", flexShrink: 0 });

const Step = styled(Btn)({ flex: "0 0 auto", minWidth: 34, padding: "3px 6px" });

const DELTAS = [-5, -1, 1, 5] as const;

export function StockPiece({
  dispatch
}: {
  dispatch: DevDispatch;
  /** Ignored: the selection is `SessionView.selected`. Kept so the caller compiles. */
  selected?: number;
}) {
  const game = useGame();
  const { selected: at, inspector } = useSessionView();
  const facts = at === undefined ? undefined : game.board.at(at);
  const structure = facts?.structure;
  const holds = Boolean(structure) || Boolean(facts?.piece);

  // **The ladders, not a fixed grade list** (§26 F2). The rows are every
  // (line, rung) this game can hold, so the menu can hand out exactly what the
  // rules can mint and nothing the reducer refuses.

  // What it is holding now, by slot, so the counts are visible beside the
  // buttons that change them — a stepper with no reading is a control you have
  // to look somewhere else to use. A structure's stock, or what the piece holds.
  const held = new Map<number, number>();
  const units = structure ? structure.stock : (facts?.units ?? []).filter((u) => u.where === "held");
  for (const unit of units) {
    const slot = LINE_INDEX[unit.line] * GRADE_SLOTS + unit.grade;
    held.set(slot, (held.get(slot) ?? 0) + 1);
  }

  const lines = ([METAL, STONE, SPECIAL] as const).filter(
    (line) => LADDERS[line].length > 0
  );
  // The selected square in the board's own labels: the inspector's card says it.
  const label = inspector?.square;

  return (
    <Fold id="stock" title={holds && label ? `Stock ${label}` : "Stock a piece — select one on the board"}>
      {lines.map((line) =>
        LADDERS[line].map((name, grade) => {
          const slot = line * GRADE_SLOTS + grade;
          return (
            <Rung key={`${line}-${grade}`}>
              <Named style={{ color: UNIT_TONE(slot) }}>{name}</Named>
              <Count role="display" size={12}>
                {held.get(slot) ?? 0}
              </Count>
              {DELTAS.map((delta) => (
                <Step
                  key={delta}
                  small
                  data-testid={`dev-material-${line}-${grade}-${delta}`}
                  disabled={!holds || (delta < 0 && (held.get(slot) ?? 0) === 0)}
                  onClick={() =>
                    dispatch({
                      type: "dev-material",
                      square: at!,
                      grade,
                      delta,
                      line
                    })
                  }
                >
                  {delta > 0 ? `+${delta}` : delta}
                </Step>
              ))}
            </Rung>
          );
        })
      )}
    </Fold>
  );
}
